/**
 * today.ts — what counts as "today", for the page that only exists on a day
 * the team is meeting.
 *
 * Pure, so `scripts/today-check.mjs` can assert it without a browser. Every
 * rule here is about a date boundary, which is exactly the kind of thing that
 * looks right all afternoon and is wrong after dinner.
 *
 * ## The trap this file exists to avoid
 *
 * `new Date().toISOString().slice(0, 10)` is the obvious way to get today's
 * date and it is the UTC date. In US Central that rolls over at 7pm, so a lead
 * opening the portal on Tuesday evening would be shown WEDNESDAY's plan and
 * told Tuesday's meeting was not happening. The portal already learned this
 * once in the other direction — `localDate` in lib/format.ts exists because
 * `new Date('2026-05-20')` is midnight UTC and renders as the 19th in any
 * negative offset. Same bug, same fix: build the date from the local parts.
 */
import type { ScheduleEntry, Workshop } from '@/lib/types'

/** Today as `YYYY-MM-DD` in the viewer's own timezone, not UTC. */
export function localDayIso(now: Date = new Date()): string {
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

/** Local midnight at the start of `now`'s day. */
export function startOfLocalDay(now: Date = new Date()): Date {
  const d = new Date(now)
  d.setHours(0, 0, 0, 0)
  return d
}

/** Local midnight at the start of the NEXT day — the exclusive upper bound. */
export function endOfLocalDay(now: Date = new Date()): Date {
  const d = startOfLocalDay(now)
  d.setDate(d.getDate() + 1)
  return d
}

/**
 * The meetings that touch today.
 *
 * A workshop counts when its span overlaps the local day rather than when it
 * starts in it: a two-day offsite is happening today on its second day, and
 * dropping it then would take the whole page away mid-event. A workshop with no
 * start time is not on the calendar at all, and a cancelled one is not a
 * meeting — both are left out, which is also what keeps them from conjuring the
 * page into existence.
 */
export function meetingsToday<W extends Pick<Workshop, 'starts_at' | 'ends_at' | 'status'>>(
  workshops: W[],
  now: Date = new Date(),
): W[] {
  const from = startOfLocalDay(now).getTime()
  const until = endOfLocalDay(now).getTime()
  return workshops
    .filter((w) => {
      if (!w.starts_at || w.status === 'cancelled') return false
      const starts = new Date(w.starts_at).getTime()
      if (Number.isNaN(starts)) return false
      const endsRaw = w.ends_at ? new Date(w.ends_at).getTime() : starts
      const ends = Number.isNaN(endsRaw) ? starts : endsRaw
      return starts < until && ends >= from
    })
    .sort((a, b) => (a.starts_at ?? '').localeCompare(b.starts_at ?? ''))
}

/**
 * The plan for today: the entries dated to it.
 *
 * `skipped` is left out — the lead has said it is not happening, and a page for
 * the day should not open with something the team has already dropped. `done`
 * stays: it happened today, and watching the day get ticked off is the point.
 */
export function entriesToday<E extends Pick<ScheduleEntry, 'scheduled_on' | 'status' | 'display_order' | 'created_at'>>(
  entries: E[],
  now: Date = new Date(),
): E[] {
  const day = localDayIso(now)
  return entries
    .filter((e) => e.scheduled_on === day && e.status !== 'skipped')
    .sort((a, b) => a.display_order - b.display_order || a.created_at.localeCompare(b.created_at))
}

/**
 * Does the Today page exist right now?
 *
 * A meeting on the calendar is what the page was asked for. A day with entries
 * on the plan and no booked meeting counts too, and that is a decision rather
 * than an oversight: the links a lead attaches to a slot are the reason to open
 * this page at all, and plenty of teams run their Tuesday off the schedule
 * without ever booking a workshop for it. Either way the rule is "something is
 * happening today", asked in one place so the nav entry and the page cannot
 * disagree about the answer.
 */
export function hasToday(meetings: unknown[], entries: unknown[]): boolean {
  return meetings.length > 0 || entries.length > 0
}

/** "Thursday, 22 October" — the subtitle, in the viewer's own locale. */
export function dayLabel(now: Date = new Date()): string {
  return now.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' })
}

/** "9:00 AM – 10:30 AM", or just the start when nothing says when it ends. */
export function timeRange(startsAt: string | null, endsAt: string | null): string | null {
  if (!startsAt) return null
  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
  return endsAt ? `${fmt(startsAt)} – ${fmt(endsAt)}` : fmt(startsAt)
}
